import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {regular, solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import {toast} from "sonner";
import {Dialog, DialogHeader, DialogContent, DialogTitle, DialogFooter} from "../../../../components/ui/dialog";
import {Label} from "../../../../components/ui/label";
import {Input} from "../../../../components/ui/input";
import {Textarea} from "../../../../components/ui/textarea";
import {Button} from "../../../../components/ui/button";
import {config} from "../../../../Environment";
import {Sparkles} from "lucide-react";
import ActivitySlider from "../../../activityComponents/ActivitySlider";

type AiAuthoringWizardProps = {
    open: boolean;
    onClose: () => void;
    isMobile: boolean;
    refreshActivities?: () => void;
    runPrescriptionById?: (id: any) => void;
};

const AiAuthoringWizardDialog: React.FC<AiAuthoringWizardProps> = ({
                                                                       open,
                                                                       onClose,
                                                                       isMobile,
                                                                       refreshActivities,
                                                                       runPrescriptionById
                                                                   }) => {
    const {t} = useTranslation();
    const retryFetch = useRetryFetch();

    // Form state
    const [activityPurpose, setActivityPurpose] = useState("");

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [aiSuggestion, setAiSuggestion] = useState<any>(null);
    const [createdActivity, setCreatedActivity] = useState<any>(null);
    const [activityStepsLimit, setActivityStepsLimit] = useState<any>(null);

    const handleNext = async () => {
        if (!activityPurpose.trim()) {
            toast.error(t("activity_purpose_required_for_save"), {
                className: "!bg-red-500 !text-white",
            });
            return;
        }
        setIsLoading(true);
        try {
            // Call your AI suggestion API
            const response = await retryFetch(
                `${config.API_BASE}/api/ai/generate-suggestion`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        purpose: activityPurpose,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to generate suggestion");
            }

            // Store the AI suggestion
            setAiSuggestion(data.conceptSuggestion);

            // Move to next step
            setCurrentStep(2);

        } catch (error) {
            console.error("Error generating suggestion:", error);
            toast.error(t("error_generating_suggestion", {error: error}));
        } finally {
            setIsLoading(false);
        }
    };

    // Handle creating the activity with AI suggestion
    const handleCreateActivity = async () => {
        setIsLoading(true);

        try {
            const response = await retryFetch(
                `${config.API_BASE}/api/ai/create-from-suggestion`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        purpose: activityPurpose,
                        suggestion: aiSuggestion,
                        stepsLimit: activityStepsLimit
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to create activity");
            }

            setCreatedActivity(data?.savedActivity);

            toast.success(t("activity_created_successfully", {name: data?.savedActivity?.name}));

            // Refresh the activities list if a refresh function was provided
            if (refreshActivities) {
                refreshActivities();
            }

            setCurrentStep(3);

            // Close the wizard
            // onClose();

        } catch (error) {
            console.error("Error creating activity:", error);
            toast.error(t("error_creating_activity") || "Error creating activity");
        } finally {
            setIsLoading(false);
        }
    };

    const resetWizard = () => {
        setCurrentStep(1);
        setAiSuggestion(null);
    };

    const renderStepIndicator = () => {
        return (
            <div className="flex justify-center items-center">
                {[1, 2, 3].map((step) => (
                    <React.Fragment key={step}>
                        <div
                            className={`${currentStep >= step ? 'bg-primary' : 'bg-gray-300'} w-4 h-4 rounded-full flex items-center justify-center`}>
                            {(currentStep > step || currentStep === 3) && (
                                <FontAwesomeIcon
                                    icon={solid("check")}
                                    className="text-white text-[9px]"
                                />
                            )}
                        </div>
                        {step < 3 && (
                            <div className={`w-8 h-1 ${currentStep >= step + 1 ? 'bg-primary' : 'bg-gray-300'}`}></div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const renderStepHeader = () => {
        switch (currentStep) {
            case 1: {
                return (
                    <>
                        <div className="italic text-xs text-gray-600">
                            <FontAwesomeIcon icon={solid("flask")}
                                             className="me-1"/>
                            {t("experimental_feature_ai_authoring")}
                        </div>
                    </>
                )
            }
            case 2:
                return (
                    <>
                        <div className="flex flex-row gap-2 items-center font-medium text-md">
                            {t("ai_suggestion") || "AI Suggestion"}
                            <Sparkles className="me-2 h-5 w-5"/>
                        </div>
                        <Input
                            value={aiSuggestion?.title}
                            onChange={(e) => setAiSuggestion({...aiSuggestion, title: e.target.value})}
                        >
                        </Input>
                    </>
                );
            case 3:
                return;
        }
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    {renderStepHeader()}
                </DialogHeader>

                {currentStep === 1 && (
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="activityPurpose">{t("activity_purpose") || "Purpose"}</Label>
                            <Textarea
                                id="activityPurpose"
                                value={activityPurpose}
                                onChange={(e) => setActivityPurpose(e.target.value)}
                                rows={3}
                            />
                        </div>
                        <Button
                            onClick={handleNext}
                            disabled={isLoading}
                            type="button"
                        >
                            {isLoading && (
                                <FontAwesomeIcon icon={solid("spinner")} className="mr-2 animate-spin"/>
                            )}
                            <FontAwesomeIcon icon={solid("wand-magic-sparkles")}
                                             className="mr-2"/> {t("create_with_ai") || "Create with AI"}
                        </Button>
                    </div>
                )}
                {currentStep === 2 && (
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Textarea
                                id="activityIntro"
                                value={aiSuggestion?.intro}
                                onChange={(e) => setAiSuggestion({...aiSuggestion, intro: e.target.value})}
                                rows={isMobile ? 9 : 6}
                            />

                            <ActivitySlider itemkey='stepsLimit'
                                            content={{
                                                label: `${t('steps_limit_for_activity')}`,
                                                width: '350px',
                                                min:2,
                                                max:10,
                                                step:1
                                            }}
                                            defaultValue={3}
                                            onChange={(key: any, value:any)=>setActivityStepsLimit(value)}></ActivitySlider>

                        </div>

                        <div className="grid  grid-cols-[1fr,2fr] gap-4">
                            <Button variant="outline" onClick={resetWizard} type="button">
                                {t("back") || "Back"}
                            </Button>
                            <Button
                                onClick={handleCreateActivity}
                                disabled={isLoading}
                                type="button"
                            >
                                {isLoading && (
                                    <FontAwesomeIcon icon={solid("spinner")} className="me-2 animate-spin"/>
                                )}
                                <FontAwesomeIcon icon={solid("wand-magic-sparkles")} className="me-2"/>

                                {t("approve_and_create") || "Approve & Create"}
                            </Button>
                        </div>
                    </div>
                )}
                {currentStep === 3 && (
                    <>
                        <div className="flex flex-col justify-center items-center gap-1 text-center">
                            <Sparkles className="me-2 h-20 w-20 fill-green-300 stroke-green-400" strokeWidth={1}/>
                            <span
                                className="text-lg font-medium">{t("activity_created_successfully", {name: createdActivity?.name})}</span>
                            <Button className="text-sm font-normal" variant="outline"
                                    onClick={() => runPrescriptionById && createdActivity._id && runPrescriptionById(createdActivity._id)}>
                                {t("click_to_start_now")}
                            </Button><p className="text-xs">
                            ({t("start_from_my_activities")})
                        </p>
                        </div>
                        <div className=" font-medium text-sm text-gray-900">
                            <FontAwesomeIcon icon={solid("pen-fancy")}
                                             className="me-1"/>
                            {t("authoring_license_for_editing_title")}
                            <div className="font-normal italic text-gray-600">
                                {t("authoring_license_for_editing_description")}
                            </div>
                            <div className="mt-2 font-normal">
                                {t("learn_more_authoring")}
                            </div>
                        </div>


                    </>
                )}
                <DialogFooter className="sm:justify-center">
                    {renderStepIndicator()}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
export default AiAuthoringWizardDialog;